import React from 'react';
import calculateDiscount from 'ui/helpers/utils/calculateDiscount';
import { Button, Counter, DiscountTag } from 'ui/components/atoms';
import { IAccessoriesDetail } from 'ui/interface/accessories.interface';
import eventClickAddCartAccessories from '../../../lib/gtm/eventClickAddCartAccessories';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';
import useImpressionAccessories from '../../../hooks/emarsys/accessories/useImpressionAccessories';
import eventClickAccessories from '../../../helpers/emarsys/accessories/eventClickAccessories';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../../store';
import eventAddToCartAccessories from '../../../helpers/emarsys/accessories/eventAddToCartAccessories';

interface IServiceFloatingActionProps {
  showCounter?: boolean;
  serviceType?: 'tintouch' | 'accessories';
  bodyTypeName?: string;
  triggerGTM?: object;
  accessoriesDetail: IAccessoriesDetail;
  quantity?: number;
  setQuantity?: (val: number) => void;
  handleSubmit?: (val: 'CART' | 'BUY') => void;
  isCMS: boolean;
  emarsysData?: {
    pageTitle: string;
    breadcrumbItems: IBreadcrumbs[];
    sku: string;
    skuPrice: string;
    carModel?: string[];
  };
}

function AccessoriesFloatingAction({
  showCounter = false,
  serviceType,
  accessoriesDetail,
  quantity,
  setQuantity,
  handleSubmit,
  isCMS = false,
  emarsysData = {
    pageTitle: '',
    breadcrumbItems: [],
    sku: '',
    skuPrice: '',
    carModel: [],
  },
}: IServiceFloatingActionProps) {
  const impressionRef = React.useRef(null);

  const dispatch = useDispatch();
  const { userData } = useSelector((state: IRootState) => state.auth) || {
    userData: null,
  };

  useImpressionAccessories(
    impressionRef,
    emarsysData?.pageTitle,
    emarsysData?.breadcrumbItems,
    emarsysData?.sku,
    emarsysData?.skuPrice,
    'subtotal',
    emarsysData?.carModel
  );

  const eventClickEmarsys = (buttonName) =>
    eventClickAccessories(
      dispatch,
      emarsysData.sku,
      emarsysData.skuPrice,
      emarsysData.pageTitle,
      emarsysData.breadcrumbItems,
      userData?.customerNo,
      'subtotal',
      buttonName,
      emarsysData?.carModel
    );

  const handleSubmitCart = (category) => {
    if (category === 'BUY') {
      eventClickEmarsys('beli langsung');
    } else if (category === 'CART') {
      eventClickEmarsys('tambah keranjang');
      eventAddToCartAccessories(
        dispatch,
        emarsysData.sku,
        emarsysData.skuPrice,
        quantity.toString(),
        emarsysData.pageTitle,
        emarsysData.breadcrumbItems,
        userData?.customerNo,
        emarsysData?.carModel
      );
    }
    handleSubmit(category);
    eventClickAddCartAccessories(
      {
        name: accessoriesDetail?.contentTitle?.toLowerCase(),
        id: accessoriesDetail?.accessoriesCode?.toLowerCase(),
        price: accessoriesDetail?.price?.value,
        brand: 'toyota',
        category: 'aksesoris',
        variant: `${accessoriesDetail?.accCategory?.toLowerCase() || 'not available'}`,
        quantity: quantity || 1,
        discountPrice: Math.floor(
          accessoriesDetail?.price?.value - accessoriesDetail?.strikeThroughPrice?.value || 0
        ),
      },
      accessoriesDetail?.voucherTitle?.toLowerCase(),
      accessoriesDetail.voucherTitle && accessoriesDetail.voucherDescription !== ''
        ? Math.floor(accessoriesDetail?.price?.value - accessoriesDetail?.strikeThroughPrice?.value)
        : 0
    );
  };
  return (
    <>
      <div
        className={`fixed bg-[#F4F4F4] w-full z-40 lg:px-[100px] transition-all lg:bottom-0 bottom-[70px] lg:border-none border-b border-[#918F8F] ${isCMS ? 'left-0 pointer-events-none' : ''}`}
        ref={impressionRef}
      >
        <div className="lg:container flex justify-between px-0 lg:py-[14px]">
          <div className="hidden justify-start items-start lg:block">
            <p className="text-sm text-reliableBlack90 font-bold mb-1">
              {accessoriesDetail?.contentTitle}
            </p>
            <div className="flex items-center">
              <p className="text-xl font-semibold text-supportiveRed mr-1">
                {accessoriesDetail?.strikeThroughPrice
                  ? accessoriesDetail?.strikeThroughPrice.formattedValue
                  : accessoriesDetail?.price?.formattedValue}
              </p>

              {accessoriesDetail?.strikeThroughPrice && (
                <>
                  <DiscountTag classNames="flex justify-center items-center bg-[#BDF2EF] px-1 rounded-sm">
                    <span className="text-xs font-normal text-reliableBlack">
                      {calculateDiscount(
                        accessoriesDetail?.price.value,
                        accessoriesDetail?.strikeThroughPrice?.value
                      )}
                    </span>
                  </DiscountTag>
                  <p className="text-base ml-3 line-through text-reliableBlack70">
                    {accessoriesDetail?.price?.formattedValue}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="w-full flex items-center justify-end gap-0 lg:gap-4 lg:w-auto">
            {serviceType === 'accessories' ? (
              <>
                {showCounter && (
                  <div className="hidden lg:block">
                    <Counter
                      feature="ACCESSORIES"
                      eventClickEmarsys={eventClickEmarsys}
                      value={quantity}
                      setValue={setQuantity}
                    />
                  </div>
                )}
                <Button
                  id={'beli-langsung-accessories'}
                  variant="bg-[#383435]"
                  className={
                    'btn-primary-white rounded-none w-full lg:w-[180px] h-[50px] lg:rounded'
                  }
                  onClick={() => handleSubmitCart('BUY')}
                >
                  <span className="text-[#FFFFFF] text-base font-bold uppercase lg:text-sm">
                    Beli Langsung
                  </span>
                </Button>

                <Button
                  onClick={() => handleSubmitCart('CART')}
                  variant="bg-supportiveRed"
                  className="w-full lg:w-[180px] h-[50px] rounded-none lg:rounded"
                >
                  <span className="text-[#FFFFFF] text-base font-bold uppercase lg:text-sm">
                    &#43; Keranjang
                  </span>
                </Button>
              </>
            ) : (
              <Button
                id={'beli-langsung-tintouch'}
                variant="bg-supportiveRed"
                className={'btn-primary-white rounded-none w-full lg:w-[180px] h-[50px] lg:rounded'}
                onClick={() => handleSubmitCart('BUY_TINTOUCH')}
              >
                <span className="text-[#FFFFFF] text-base font-bold uppercase lg:text-sm">
                  Beli Langsung
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AccessoriesFloatingAction;
